import { z } from 'zod/v4'

import { jsonDocument } from '../../contract/index.js'
import { defineScreen, type ScreenData } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

/*
 * «Conectores API» › the list, the new mapping and the detail of an exchange. The list and the form read the SAME
 * complete catalogue (the page does not paginate: it filters in the browser); the detail has the exchange as its
 * subject and the saved API configuration and what depends on it as sections. Each schema mirrors what the domain
 * returns (`loadApiMappingCatalogue`, `toApiConfigView`, `loadApiConfigBlastRadius`) and the type assertions of
 * `packages/backend/src/platform/server/__tests__/shared-api-import-mappings-screen.type-assertions.ts` tie them.
 */

export const apiMappingExchangeRowSchema = z.strictObject({
  slug: z.string(),
  /** `exchanges.name`, the column customers see; not `display_name`. */
  name: z.string(),
  logoUrl: z.string(),
  /** An adapter loaded in the registry of the process. */
  adapterAvailable: z.boolean(),
  supportsApiImport: z.boolean(),
  templateVersion: z.string().nullable(),
  rulesCount: z.number().int(),
  apiConfigVersion: z.string().nullable(),
})

export const apiMappingConfigSchema = z.strictObject({
  id: z.string(),
  exchangeSlug: z.string(),
  baseUrl: z.string(),
  testnetUrl: z.string().nullable(),
  timeoutMs: z.number().int(),
  maxRequestsPerMinute: z.number().int(),
  maxRequestsPerSecond: z.number().int().nullable(),
  rateLimitMode: z.enum(['requests', 'weight']),
  maxWeightPerMinute: z.number().int().nullable(),
  bodyEncoding: z.enum(['form', 'json']),
  authScheme: z.string(),
  authConfig: jsonDocument({
    reason:
      'auth scheme configuration: one of seven shapes chosen by authScheme, edited as JSON by the admin form',
    maxBytes: 8_000,
  }),
  responseWrapper: jsonDocument({
    reason: 'response envelope description of the exchange, edited as JSON by the admin form',
    maxBytes: 4_000,
  }).nullable(),
  supportsSubAccounts: z.boolean().nullable(),
  supportsTestnet: z.boolean().nullable(),
  isActive: z.boolean().nullable(),
  version: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
})

export const apiMappingUsageSchema = z.strictObject({
  endpointCount: z.number().int(),
  savedVersionCount: z.number().int(),
  connectedDataSourceCount: z.number().int(),
  affectedTenantCount: z.number().int(),
  registeredApiKeyCount: z.number().int(),
  templateCount: z.number().int(),
  activeTemplateCount: z.number().int(),
  publishedTemplateCount: z.number().int(),
  isInUse: z.boolean(),
})

const catalogueResponse = z.strictObject({
  exchanges: screenSection(z.array(apiMappingExchangeRowSchema)),
  freshness: freshnessSchema,
})

export const adminApiImportMappingsScreen = defineScreen({
  id: 'admin.apiImportMappings',
  path: '/api/screens/admin/api-import-mappings',
  access: 'super_admin',
  query: z.strictObject({}),
  response: catalogueResponse,
  dependsOn: ['admin.exchanges'],
  budget: { requests: 1, statements: 6, bytes: 60_000, p95Ms: 250 },
  summary:
    'API connectors: the complete exchange catalogue with adapter, template and saved API configuration of each',
})

export const adminApiImportMappingNewScreen = defineScreen({
  id: 'admin.apiImportMappings.new',
  path: '/api/screens/admin/api-import-mappings/new',
  access: 'super_admin',
  query: z.strictObject({}),
  response: catalogueResponse,
  dependsOn: ['admin.exchanges'],
  budget: { requests: 1, statements: 6, bytes: 60_000, p95Ms: 250 },
  summary: 'API connectors › new mapping: the exchange catalogue the form picks from',
})

export const adminApiImportMappingDetailScreen = defineScreen({
  id: 'admin.apiImportMappings.detail',
  path: '/api/screens/admin/api-import-mappings/detail',
  access: 'super_admin',
  query: z.strictObject({ slug: z.string().min(1).max(100) }),
  response: z.strictObject({
    /** The subject: an unknown slug is the 404 `EXCHANGE_NOT_FOUND` of the whole screen. */
    exchange: apiMappingExchangeRowSchema,
    /** `data: null`: the exchange has no saved configuration yet; it is not a failure. */
    config: screenSection(apiMappingConfigSchema.nullable()),
    usage: screenSection(apiMappingUsageSchema),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.exchanges'],
  budget: { requests: 1, statements: 14, bytes: 20_000, p95Ms: 250 },
  summary:
    'API connectors › one exchange: the exchange, its saved API configuration (null when none) and what depends on it',
})
export type ApiMappingExchangeRow = z.infer<typeof apiMappingExchangeRowSchema>
export type ApiMappingConfig = z.infer<typeof apiMappingConfigSchema>
export type ApiMappingUsage = z.infer<typeof apiMappingUsageSchema>
export type AdminApiImportMappingsData = ScreenData<typeof adminApiImportMappingsScreen>
export type AdminApiImportMappingNewData = ScreenData<typeof adminApiImportMappingNewScreen>
export type AdminApiImportMappingDetailData = ScreenData<typeof adminApiImportMappingDetailScreen>
